<template>
    <div
        ref="takeoverRef"
        class="c-video-takeover"
        :data-state="isInView ? 'visible' : 'hidden'"
        data-testid="videotakeover"
    >
        <DsBlock background="neutral" class="c-video-takeover__block">
            <div class="c-video-takeover__container">
                <Transition name="content">
                    <div
                        v-show="isInView && playbackType === 'bg'"
                        class="c-video-takeover__content"
                        :inert="playbackType === 'full'"
                    >
                        <blockquote class="c-video-takeover__testimonial">
                            <DsText
                                v-if="quoteText"
                                class="c-video-takeover__quote"
                                text-style="medium"
                                text-type="heading-2xl"
                            >
                                {{ quoteText }}
                            </DsText>
                            <DsText
                                v-if="quoteName"
                                class="c-video-takeover__name"
                                text-style="medium"
                                text-type="heading-lg"
                            >
                                {{ quoteName }}
                            </DsText>
                            <DsText
                                v-if="quoteRole"
                                class="c-video-takeover__role"
                                text-type="body-xs"
                            >
                                {{ quoteRole }}
                            </DsText>
                        </blockquote>
                        <DsButton
                            icon-start="button-play"
                            variant="primary-inverted"
                            :click-handler="switchToFullPlayback"
                            data-testid="watchButton"
                        >
                            {{ buttonText }}
                        </DsButton>
                    </div>
                </Transition>

                <Transition name="content">
                    <DsButtonPlayProgress
                        v-if="videoBgVimeoUrl"
                        v-show="isInView && playbackType === 'bg'"
                        ref="progressButton"
                        class="c-video-takeover__progress-button"
                        :aria-label-play="ariaLabelPlay"
                        :play-handler="(_e: MouseEvent) => bgPlay()"
                        :aria-label-pause="ariaLabelPause"
                        :initial-state="videoPlayState"
                        :progress="videoProgress"
                        :pause-handler="(_e: MouseEvent) => bgPause()"
                    ></DsButtonPlayProgress>
                </Transition>

                <div
                    class="c-video-takeover__video-wrapper c-video-takeover__video-wrapper--bg"
                    data-testid="video-wrapper-bg"
                    :class="dynamicBgVideoClass"
                    :inert="playbackType === 'full'"
                    :style="`--poster-img: url(${posterImageSrc})`"
                >
                    <div class="c-video-takeover__video-overlay"></div>
                    <vueVimeoPlayer
                        v-if="videoBgVimeoUrl"
                        ref="vimeoBgPlayer"
                        data-testid="video-bg-player"
                        tabindex="-1"
                        class="c-video-takeover__video"
                        :video-url="`${videoBgVimeoUrl}`"
                        :options="{
                            autopause: true,
                            autoplay: false,
                            controls: false,
                            dnt: true,
                            loop: true,
                            muted: true,
                        }"
                        @ready="onBgReady"
                        @playing="onPlaying"
                        @timeupdate="timeupdateHandler"
                    ></vueVimeoPlayer>
                    <img
                        v-if="posterImageSrc"
                        class="c-video-takeover__poster-img"
                        :src="posterImageSrc"
                        alt=""
                    />
                </div>

                <div
                    class="c-video-takeover__video-wrapper c-video-takeover__video-wrapper--full"
                    data-testid="video-wrapper-full"
                    :class="dynamicFullVideoClass"
                    :inert="playbackType === 'bg'"
                >
                    <DsIconButton
                        class="c-video-takeover__close"
                        :click-handler="showBackgroundVideo"
                        :aria-label="ariaLabelClose"
                        icon-name="button-close"
                        variant="ghost-inverted"
                    ></DsIconButton>
                    <vueVimeoPlayer
                        ref="vimeoPlayer"
                        class="c-video-takeover__vimeo"
                        :video-url="`${videoFullVimeoUrl}`"
                        :options="{
                            autopause: true,
                            background: false,
                            controls: true,
                            dnt: true,
                            loop: false,
                            muted: true,
                            autoplay: false,
                        }"
                        @ready="onReady"
                    ></vueVimeoPlayer>
                </div>
            </div>
        </DsBlock>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useIntersectionObserver } from "@vueuse/core";
import { vueVimeoPlayer } from "vue-vimeo-player";
import { DsBlock, DsButton, DsButtonPlayProgress, DsText, DsIconButton } from "@coloplast/design-system";
import "./video-takeover.scss";

interface VideoTakeoverProps {
    /**
     * Accessible label for the play button
     */
    ariaLabelPlay?: string;
    /**
     * Accessible label for the pause button
     */
    ariaLabelPause?: string;
    /**
     * Accessible label for the close button
     */
    ariaLabelClose?: string;
    /**
     * Button text for the CTA that triggers the video playback
     */
    buttonText: string;
    /**
     * An image that's displayed while the bg video loads
     */
    posterImageSrc?: string;
    /**
     * Name of the person the quote is attributed to
     */
    quoteName?: string;
    /**
     * The role of the person the quote is from, e.g. user of a product, job role, etc
     */
    quoteRole?: string;
    /**
     * Large quote text
     */
    quoteText?: string;
    /**
     * The url of the full video
     */
    videoFullVimeoUrl: string;
    /**
     * The url of the background video (should be a short video loop ~5 seconds)
     */
    videoBgVimeoUrl?: string;
}

withDefaults(defineProps<VideoTakeoverProps>(), {
    ariaLabelClose: "Close",
    ariaLabelPause: "Pause",
    ariaLabelPlay: "Play",
});

// Refs and state
const takeoverRef = ref<HTMLElement | null>(null);
const isInView = ref(false);
const hasBeenInView = ref(false);
const vimeoPlayer = ref<typeof vueVimeoPlayer>();
const vimeoBgPlayer = ref<typeof vueVimeoPlayer>();
const vimeoPlayerReady = ref(false);
const vimeoBgPlayerReady = ref(false);
const playbackType = ref<"bg" | "full">("bg");
const progressButton = ref<InstanceType<typeof DsButtonPlayProgress> | null>(
    null,
);
const userPlayState = ref<"pause" | "play" | undefined>("play");
const videoProgress = ref(0);
const videoPlayState = ref<"pause" | "play" | undefined>("pause");
const hidePoster = ref(false);

// Computed props and class helper
const dynamicVideoClass = (
    playerReady: boolean,
    type: "bg" | "full",
    currentType: "bg" | "full",
    hidePoster: boolean,
) => {
    const classes = [];
    if (playerReady) classes.push("c-video-takeover__video-wrapper--ready");
    if (type === currentType)
        classes.push("c-video-takeover__video-wrapper--active");
    if (hidePoster) {
        classes.push("c-video-takeover__video-wrapper--hide-poster");
    }
    return classes;
};

// Update calls to use the new utility function
const dynamicBgVideoClass = computed(() =>
    dynamicVideoClass(
        vimeoBgPlayerReady.value,
        "bg",
        playbackType.value,
        hidePoster.value,
    ),
);
const dynamicFullVideoClass = computed(() =>
    dynamicVideoClass(
        vimeoPlayerReady.value,
        "full",
        playbackType.value,
        hidePoster.value,
    ),
);

// Video event handling
const onReady = () => {
    vimeoPlayerReady.value = true;
};

const onBgReady = async () => {
    vimeoBgPlayerReady.value = true;
};

const onPlaying = () => {
    if (hidePoster.value === false) {
        hidePoster.value = true;
    }
};

// Action queue
const actionQueue: (() => Promise<void>)[] = [];
let isProcessing = false;

const processQueue = async () => {
    if (isProcessing) return;

    isProcessing = true;
    try {
        while (actionQueue.length > 0) {
            const action = actionQueue.shift();
            if (!action) break;
            await action(); // Wait for each action to finish
        }
    } catch (error) {
        console.error("Action failed", error);
    } finally {
        isProcessing = false;
    }
};

const enqueueAction = (action: () => Promise<void>) => {
    actionQueue.push(action);
    processQueue();
};

// Lifecycle hooks
onMounted(() => {
    globalThis.addEventListener("keydown", handleEscapeKey);
});

onUnmounted(() => {
    globalThis.removeEventListener("keydown", handleEscapeKey);
});

// Intersection Observer
useIntersectionObserver(
    takeoverRef,
    ([entry]) => {
        isInView.value = entry?.isIntersecting || false;
    },
    { threshold: 0.75 },
);

// Video Control Methods
const playWithTimeout = async (
    player: typeof vueVimeoPlayer,
    timeoutMs = 1000,
) => {
    return new Promise<void>((resolve, reject) => {
        let timeout = setTimeout(() => {
            console.warn("Vimeo play() timed out");
            reject(new Error("Vimeo play() timed out"));
        }, timeoutMs);

        player
            .play()
            .then(() => {
                clearTimeout(timeout);
                resolve();
            })
            .catch((err: Error) => {
                clearTimeout(timeout);
                reject(err);
            });
    });
};

const pauseWithTimeout = async (
    player: typeof vueVimeoPlayer,
    timeoutMs = 500,
) => {
    return new Promise<void>((resolve, reject) => {
        let timeout = setTimeout(() => {
            console.warn("Vimeo pause() timed out");
            reject(new Error("Vimeo pause() timed out"));
        }, timeoutMs);

        player
            .pause()
            .then(() => {
                clearTimeout(timeout);
                resolve();
            })
            .catch((err: Error) => {
                clearTimeout(timeout);
                reject(err);
            });
    });
};

const bgPlay = async (userTriggered: boolean = true) => {
    enqueueAction(async () => {
        try {
            if (vimeoBgPlayer.value && "play" in vimeoBgPlayer.value) {
                await playWithTimeout(vimeoBgPlayer.value);
            }
        } catch (err) {
            console.error(err);
        }
        updatePlayState(true);
        if (userTriggered) {
            userPlayState.value = "play";
        }
    });
};

const bgPause = async (userTriggered: boolean = true) => {
    enqueueAction(async () => {
        try {
            if (vimeoBgPlayer.value && "pause" in vimeoBgPlayer.value) {
                await pauseWithTimeout(vimeoBgPlayer.value);
            }
        } catch (err) {
            console.error(err);
        }
        updatePlayState(false);
        if (userTriggered) {
            userPlayState.value = "pause";
        }
    });
};

const switchToFullPlayback = () => {
    playbackType.value = "full";
};

const timeupdateHandler = (event: {
    seconds: number;
    percent: number;
    duration: number;
}) => {
    videoProgress.value = event.percent;
};

const updatePlayState = (isPlaying: boolean) => {
    progressButton.value?.setButtonState(isPlaying);
};

const showBackgroundVideo = async () => {
    if (vimeoPlayer.value) {
        vimeoPlayer.value?.pause();
    }
    playbackType.value = "bg";
    if (userPlayState.value !== "pause") {
        await bgPlay(false);
    }
};

const freezeBackgroundVideo = async () => {
    await bgPause(false);
    vimeoPlayer.value?.pause();
    playbackType.value = "bg";
};

const showFullVideo = async () => {
    await bgPause(false);
    vimeoPlayer.value?.mute();
    vimeoPlayer.value?.play();
    vimeoPlayer.value?.unmute();
};

// Event handler for Escape key
const handleEscapeKey = (event: KeyboardEvent) => {
    if (event.key === "Escape" && playbackType.value === "full") {
        showBackgroundVideo();
    }
};

// Watchers
watch(isInView, async (newVal: boolean) => {
    if (!vimeoBgPlayer.value) return;
    if (newVal) {
        hasBeenInView.value = true;
        showBackgroundVideo();
    } else {
        freezeBackgroundVideo();
    }
});

watch(playbackType, (newVal: "bg" | "full") => {
    if (newVal === "full") {
        showFullVideo();
    }
});
</script>
